import { Avatar, Box, Button, Divider, Menu, Portal, Typography } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';

export const createInitialNotifications = () => ([
  {
    id: 'access-request',
    actor: 'Lex Murphy',
    action: 'requested access to',
    target: 'UNIX directory tree hierarchy',
    timestamp: 'Today at 9:42 AM',
    initials: 'LM',
    avatarColor: '#0f766e',
    unread: true,
    actions: ['Approve', 'Decline'],
  },
  {
    id: 'comments',
    actor: 'Ray Arnold',
    action: 'left 6 comments on',
    target: 'Isla Nublar SOC2 compliance report',
    timestamp: 'Last Wednesday at 9:42 AM',
    initials: 'RA',
    avatarColor: '#1d4ed8',
    unread: true,
  },
  {
    id: 'reply',
    actor: 'Denise Nedry',
    action: 'replied to',
    target: 'Anna Srzand',
    timestamp: 'Last Wednesday at 9:42 AM',
    initials: 'DN',
    avatarColor: '#c2410c',
    preview: 'Oh, I finished de-bugging the phones, but the system\'s compiling for eighteen minutes, or twenty...',
  },
  {
    id: 'attachment',
    actor: 'John Hammond',
    action: 'attached a file to',
    target: 'Isla Nublar SOC2 compliance report',
    timestamp: 'Last Wednesday at 9:42 AM',
    initials: 'JH',
    avatarColor: '#57534e',
    attachment: 'EY_review.pdf',
  },
  {
    id: 'favorite',
    actor: 'Denise Nedry',
    action: 'commented on',
    target: 'Isla Nublar SOC2 compliance report',
    timestamp: 'Last Wednesday at 9:42 AM',
    initials: 'DN',
    avatarColor: '#2563eb',
    preview: 'Oh, I finished de-bugging the phones, but the system\'s compiling for eighteen minutes, or twenty...',
    favoriteLabel: 'Add to favorites',
  },
  {
    id: 'new-account',
    actor: 'New Account',
    action: 'created',
    timestamp: 'Last Wednesday at 9:42 AM',
    system: true,
  },
]);

function NotificationItem({ item, onClick }) {
  const isSystemItem = item.system;

  return (
    <Box
      onClick={() => onClick(item)}
      sx={{
        position: 'relative',
        px: 2,
        py: 1.15,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': { bgcolor: '#f8fafc' },
      }}
    >
      {item.unread && (
        <Box
          sx={{
            position: 'absolute',
            left: 10,
            top: 19,
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: '#60a5fa',
          }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        {isSystemItem ? (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: '#eef2ff',
              color: '#4f46e5',
            }}
          >
            <BoltRoundedIcon sx={{ fontSize: 16 }} />
          </Avatar>
        ) : (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: item.avatarColor,
            }}
          >
            {item.initials}
          </Avatar>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: '#111827',
              fontSize: 13,
              lineHeight: 1.45,
              fontFamily: 'Poppins',
            }}
          >
            {isSystemItem ? (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>{item.actor}</Box> {item.action}
              </>
            ) : (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>{item.actor}</Box> {item.action}{' '}
                {item.target && <Box component="span" sx={{ fontWeight: 700 }}>{item.target}</Box>}
              </>
            )}
          </Typography>

          {item.actions && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1.1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={(event) => event.stopPropagation()}
                sx={{
                  minWidth: 0,
                  px: 1.1,
                  py: 0.35,
                  fontSize: 11,
                  lineHeight: 1.1,
                  fontWeight: 600,
                  textTransform: 'none',
                  bgcolor: '#f97316',
                  borderRadius: '6px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#ea580c',
                    boxShadow: 'none',
                  },
                }}
              >
                {item.actions[0]}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => event.stopPropagation()}
                sx={{
                  minWidth: 0,
                  px: 1.1,
                  py: 0.35,
                  fontSize: 11,
                  lineHeight: 1.1,
                  fontWeight: 500,
                  textTransform: 'none',
                  color: '#4b5563',
                  borderColor: '#d1d5db',
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    bgcolor: '#f9fafb',
                  },
                }}
              >
                {item.actions[1]}
              </Button>
            </Box>
          )}

          {item.preview && (
            <Typography
              sx={{
                mt: 1,
                color: '#4b5563',
                fontSize: 12.5,
                lineHeight: 1.45,
                fontFamily: 'Poppins',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                pl: 1.1,
                borderLeft: '3px solid #e5e7eb',
              }}
            >
              {`"${item.preview}"`}
            </Typography>
          )}

          {item.attachment && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 1,
                px: 0.8,
                py: 0.45,
                borderRadius: '8px',
                bgcolor: '#f9fafb',
                border: '1px solid #e5e7eb',
              }}
            >
              <InsertDriveFileRoundedIcon sx={{ fontSize: 14, color: '#ef4444' }} />
              <Typography sx={{ fontSize: 12, color: '#4b5563', fontFamily: 'Poppins' }}>
                {item.attachment}
              </Typography>
            </Box>
          )}

          {item.favoriteLabel && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                mt: 1,
                px: 1,
                py: 0.55,
                borderRadius: '999px',
                bgcolor: '#fff',
                border: '1px solid #d1d5db',
              }}
            >
              <StarBorderRoundedIcon sx={{ fontSize: 14, color: '#4b5563' }} />
              <Typography sx={{ fontSize: 12.5, color: '#374151', fontFamily: 'Poppins' }}>
                {item.favoriteLabel}
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              mt: 0.9,
              color: '#9ca3af',
              fontSize: 12,
              fontFamily: 'Poppins',
            }}
          >
            {item.timestamp}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function NotificationDropdown({
  anchorEl,
  open,
  onClose,
  notifications,
  onMarkAllRead,
  onNotificationClick,
  onViewAll,
  notificationsEnabled,
}) {
  return (
    <>
      {open && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1299,
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(15, 23, 42, 0.12)',
              pointerEvents: 'none',
            }}
          />
        </Portal>
      )}
      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: '10px !important',
              width: '380px !important',
              minWidth: '380px !important',
              maxWidth: 'calc(100vw - 24px) !important',
              borderRadius: '24px !important',
              overflow: 'hidden !important',
              border: '1px solid #e5e7eb !important',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18) !important',
              '& .MuiMenu-list': {
                p: 0,
                overflow: 'hidden',
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 18,
                width: 12,
                height: 12,
                bgcolor: '#ffffff',
                borderTop: '1px solid #e5e7eb',
                borderLeft: '1px solid #e5e7eb',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            width: '360px !important',
            minWidth: '360px !important',
            maxWidth: 'calc(100vw - 24px) !important',
            borderRadius: '24px !important',
            overflow: 'hidden !important',
            border: '1px solid #e5e7eb !important',
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18) !important',
            mt: '10px !important',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 18,
              width: 12,
              height: 12,
              bgcolor: '#ffffff',
              borderTop: '1px solid #e5e7eb',
              borderLeft: '1px solid #e5e7eb',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
          '& .MuiMenu-list': {
            p: 0,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ px: 1.75, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#111827', fontWeight: 600, fontSize: 13, fontFamily: 'Poppins' }}>
              Notifications
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Typography sx={{ color: '#9ca3af', fontSize: 12, fontFamily: 'Poppins' }}>
                All
              </Typography>
              <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
            </Box>
          </Box>

          <Box
            onClick={onMarkAllRead}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#374151',
              fontSize: 12,
              fontWeight: 500,
              fontFamily: 'Poppins',
              cursor: 'pointer',
            }}
          >
            <Typography sx={{ fontSize: 12, fontFamily: 'Poppins' }}>Mark all as read</Typography>
            <CheckCircleOutlineRoundedIcon sx={{ fontSize: 15 }} />
          </Box>
        </Box>

        <Divider />

        {!notificationsEnabled ? (
          <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
            <Typography sx={{ color: '#111827', fontSize: 14, fontWeight: 600, fontFamily: 'Poppins' }}>
              Notifications are turned off
            </Typography>
            <Typography sx={{ mt: 0.8, color: '#6b7280', fontSize: 12.5, fontFamily: 'Poppins' }}>
              Enable system notifications in settings to receive announcement updates here.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: 360,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#cbd5e1',
                borderRadius: '999px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#94a3b8',
              },
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 transparent',
            }}
          >
            {notifications.map((item, index) => (
              <Box key={item.id}>
                <NotificationItem item={item} onClick={onNotificationClick} />
                {index < notifications.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}

        <Divider />

        <Box sx={{ p: 1.25 }}>
          <Button
            fullWidth
            variant="text"
            onClick={onViewAll}
            sx={{
              borderRadius: '10px',
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              color: '#2563eb',
              fontFamily: 'Poppins',
              '&:hover': { bgcolor: '#eff6ff' },
            }}
          >
            View all notifications
          </Button>
        </Box>
      </Menu>
    </>
  );
}